import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  ArrowLeft, Mail, Phone, MapPin, FileText, Receipt, Image as ImageIcon,
  Euro, Loader2, ExternalLink, Download, UserCheck, UserX, Briefcase, Send
} from 'lucide-react';
import { toast } from 'sonner';

const fmtEuro = (v) => `€ ${(Number(v) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatusBadge = ({ status, type }) => {
  if (!status) return null;
  const cfg = {
    draft: { bg: 'bg-gray-100', txt: 'text-gray-700', label: 'Concept' },
    sent: { bg: 'bg-blue-100', txt: 'text-blue-700', label: 'Verzonden' },
    goedgekeurd: { bg: 'bg-green-100', txt: 'text-green-700', label: 'Goedgekeurd' },
    afgewezen: { bg: 'bg-red-100', txt: 'text-red-700', label: 'Afgewezen' },
    betaald: { bg: 'bg-emerald-100', txt: 'text-emerald-700', label: 'Betaald' },
    paid: { bg: 'bg-emerald-100', txt: 'text-emerald-700', label: 'Betaald' },
    open: { bg: 'bg-amber-100', txt: 'text-amber-700', label: 'Open' },
  }[status] || { bg: 'bg-gray-100', txt: 'text-gray-600', label: status };
  return <span className={`text-xs px-2 py-0.5 rounded font-medium ${cfg.bg} ${cfg.txt}`}>{cfg.label}</span>;
};

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axiosInstance.get(`/clients/${id}/360`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        toast.error('Klant niet gevonden');
        navigate('/clients');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-8 h-8 animate-spin text-[#3da5a5]" />
    </div>
  );
  if (!data) return null;

  const { client, portal_account, has_portal_account, quotes, invoices, photos, work_requests, totals } = data;
  const addresses = client.addresses || [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/clients')} data-testid="back-to-clients">
          <ArrowLeft className="w-4 h-4 mr-1" /> Terug naar Bedrijven
        </Button>
      </div>

      {/* Header */}
      <Card>
        <CardContent className="py-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800" data-testid="client-name">{client.name}</h1>
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
                {client.email && <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{client.email}</span>}
                {client.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{client.phone}</span>}
                {client.address && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{client.address}</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {has_portal_account ? (
                <span className="bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1" data-testid="portal-account-badge">
                  <UserCheck className="w-4 h-4" /> Klantenportaal account
                </span>
              ) : (
                <span className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1" data-testid="no-portal-account-badge">
                  <UserX className="w-4 h-4" /> Geen portaal account
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="py-4">
          <p className="text-xs text-gray-500">Gefactureerd</p>
          <p className="text-2xl font-bold text-[#1a5276]" data-testid="stat-invoiced">{fmtEuro(totals.invoiced)}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs text-gray-500">Betaald (incl. aanbetaling)</p>
          <p className="text-2xl font-bold text-emerald-600" data-testid="stat-paid">{fmtEuro(totals.paid)}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs text-gray-500">Openstaand</p>
          <p className="text-2xl font-bold text-amber-600" data-testid="stat-open">{fmtEuro(totals.open)}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs text-gray-500">Foto's</p>
          <p className="text-2xl font-bold text-gray-800" data-testid="stat-photos">{totals.photo_count}</p>
        </CardContent></Card>
      </div>

      {/* Addresses */}
      {addresses.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><MapPin className="w-5 h-5 text-[#3da5a5]" /> Adressen</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {addresses.map(a => (
                <div key={a.id} className="border border-gray-200 rounded p-3 text-sm" data-testid={`address-${a.id}`}>
                  {a.label && <p className="text-[#3da5a5] font-semibold">{a.label}</p>}
                  <p className="text-gray-700">{a.address}</p>
                  {a.email && <p className="text-gray-500 text-xs flex items-center gap-1 mt-1"><Mail className="w-3 h-3" />{a.email}</p>}
                  {a.phone && <p className="text-gray-500 text-xs flex items-center gap-1"><Phone className="w-3 h-3" />{a.phone}</p>}
                  {a.is_primary && <span className="text-xs text-yellow-600">⭐ Primair</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Offertes */}
      <Card data-testid="quotes-section">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="w-5 h-5 text-blue-500" /> Offertes ({totals.quote_count})</CardTitle></CardHeader>
        <CardContent>
          {quotes.length === 0 ? (
            <p className="text-gray-400 text-sm py-4 text-center">Nog geen offertes</p>
          ) : (
            <div className="space-y-2">
              {quotes.map(q => (
                <div key={q.id} className="flex items-center justify-between border-b border-gray-100 last:border-0 py-2 text-sm" data-testid={`quote-row-${q.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="font-semibold text-[#1a5276]">{q.number}</p>
                    <StatusBadge status={q.status} type="quote" />
                    {q.mollie_payment?.status === 'paid' && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">€ Aanbetaling betaald</span>}
                    {q.mollie_payment?.status === 'open' && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">€ Aanbetaling open</span>}
                    <span className="text-xs text-gray-400">{q.offer_date}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-bold text-gray-700">{fmtEuro(q.total)}</p>
                    {q.public_token && (
                      <Button variant="ghost" size="sm" onClick={() => window.open(`/offerte/${q.public_token}`, '_blank')} title="Open klantweergave">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Facturen */}
      <Card data-testid="invoices-section">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Receipt className="w-5 h-5 text-purple-500" /> Facturen ({totals.invoice_count})</CardTitle></CardHeader>
        <CardContent>
          {invoices.length === 0 ? (
            <p className="text-gray-400 text-sm py-4 text-center">Nog geen facturen</p>
          ) : (
            <div className="space-y-2">
              {invoices.map(inv => (
                <div key={inv.id} className="flex items-center justify-between border-b border-gray-100 last:border-0 py-2 text-sm" data-testid={`invoice-row-${inv.id}`}>
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className={`font-semibold ${inv.type === 'credit' ? 'text-red-600' : 'text-[#1a5276]'}`}>{inv.number}</p>
                    {inv.type === 'credit' && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">CREDIT</span>}
                    {inv.mollie_payment?.status === 'paid' && <StatusBadge status="betaald" />}
                    {inv.mollie_payment?.status === 'open' && <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">€ Restbedrag open</span>}
                    {inv.down_payment?.status === 'paid' && !inv.mollie_payment && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">✓ Aanbetaling</span>}
                    <span className="text-xs text-gray-400">{inv.invoice_date}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className={`font-bold ${inv.type === 'credit' ? 'text-red-600' : 'text-gray-700'}`}>{fmtEuro(inv.total_incl)}</p>
                    {inv.public_token && (
                      <Button variant="ghost" size="sm" onClick={() => window.open(`/factuur/${inv.public_token}`, '_blank')} title="Open klantweergave">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Work requests */}
      {work_requests.length > 0 && (
        <Card data-testid="workrequests-section">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Briefcase className="w-5 h-5 text-orange-500" /> Werkaanvragen ({work_requests.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {work_requests.map(wr => (
                <div key={wr.id} className="flex items-center justify-between border-b border-gray-100 last:border-0 py-2 text-sm">
                  <div>
                    <p className="font-medium text-gray-800">{wr.title}</p>
                    <p className="text-xs text-gray-500">{wr.service_type} · {wr.status}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Photos */}
      <Card data-testid="photos-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><ImageIcon className="w-5 h-5 text-pink-500" /> Foto's ({photos.length})</CardTitle>
            {photos.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => navigate(`/photos?client=${encodeURIComponent(client.name)}`)} title="Open in galerij">
                Open galerij <ExternalLink className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {photos.length === 0 ? (
            <p className="text-gray-400 text-sm py-4 text-center">Nog geen foto's</p>
          ) : (
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2">
              {photos.slice(0, 18).map((p, i) => (
                <a
                  key={p.id || i}
                  href={p.cloudinary_url || p.url}
                  target="_blank"
                  rel="noreferrer"
                  className="aspect-square block rounded overflow-hidden bg-gray-100 hover:opacity-90 transition"
                  title={`${p.entry_client || ''} — ${p.entry_date || ''}`}
                  data-testid={`photo-thumb-${i}`}
                >
                  <img
                    loading="lazy"
                    src={p.cloudinary_thumbnail || p.thumbnail || p.cloudinary_url || p.url}
                    alt={p.entry_client || 'foto'}
                    className="w-full h-full object-cover"
                  />
                </a>
              ))}
            </div>
          )}
          {photos.length > 18 && (
            <p className="text-xs text-gray-500 text-center mt-3">
              ... en nog {photos.length - 18} foto's — open de galerij voor alles
            </p>
          )}
        </CardContent>
      </Card>

      {/* Portal account */}
      {portal_account && (
        <Card data-testid="portal-account-section">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><UserCheck className="w-5 h-5 text-green-500" /> Klantenportaal account</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-1">
            <p><strong>Naam:</strong> {portal_account.name}</p>
            <p><strong>E-mail:</strong> {portal_account.email}</p>
            {portal_account.phone && <p><strong>Telefoon:</strong> {portal_account.phone}</p>}
            {portal_account.company_name && <p><strong>Bedrijf:</strong> {portal_account.company_name}</p>}
            <p className="text-xs text-gray-400">Geregistreerd op: {portal_account.created_at}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientDetail;
